import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { CheckCircle2, Lock, Clock, AlertTriangle, PlayCircle, RotateCcw, BookOpen, Route } from 'lucide-react';
import { motion } from 'framer-motion';
import TrainingSchedule from './TrainingSchedule';

export const STATUS_META = {
  approved: { label: 'Signed off', badge: 'bg-green-100 text-green-700', icon: CheckCircle2, ring: 'bg-[#1B2A4A] border-[#1B2A4A] text-white' },
  pending_signoff: { label: 'Awaiting sign-off', badge: 'bg-amber-100 text-amber-700', icon: Clock, ring: 'bg-amber-400 border-amber-400 text-white' },
  rejected: { label: 'Sent back', badge: 'bg-red-100 text-red-700', icon: AlertTriangle, ring: 'bg-red-500 border-red-500 text-white' },
  in_progress: { label: 'In progress', badge: 'bg-orange-100 text-[#D9480F]', icon: PlayCircle, ring: 'bg-[#D9480F] border-[#D9480F] text-white ring-4 ring-[#D9480F]/20' },
  available: { label: 'Up next', badge: 'bg-orange-100 text-[#D9480F]', icon: PlayCircle, ring: 'bg-[#D9480F] border-[#D9480F] text-white ring-4 ring-[#D9480F]/20' },
  locked: { label: 'Locked', badge: 'bg-stone-100 text-stone-500', icon: Lock, ring: 'bg-stone-100 border-stone-200 text-stone-300' },
};

function PathModuleRow({ mod, index, isLast, navigate }) {
  const meta = STATUS_META[mod.status] || STATUS_META.locked;
  const Icon = meta.icon;
  const isLocked = mod.status === 'locked';
  const isCurrent = mod.status === 'available' || mod.status === 'in_progress' || mod.status === 'rejected';
  return (
    <motion.div initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }}
      className="relative flex gap-4" data-testid={`path-module-${mod.id}`}>
      <div className="flex flex-col items-center shrink-0">
        <div className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${meta.ring}`} data-testid={`path-module-status-${mod.id}`}>
          <Icon className="w-4 h-4" />
        </div>
        {!isLast && <div className={`w-0.5 flex-1 my-1 ${mod.status === 'approved' ? 'bg-[#1B2A4A]' : 'bg-stone-200'}`} />}
      </div>
      <Card onClick={() => !isLocked && navigate(`/training/${mod.id}`)}
        className={`flex-1 mb-4 rounded-2xl border transition-all ${isLocked ? 'bg-stone-50 border-stone-200 opacity-70' : 'bg-white border-stone-200 hover:shadow-md hover:border-stone-300 cursor-pointer'} ${isCurrent ? 'border-[#D9480F]/40 shadow-sm' : ''}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">Module {index + 1}</p>
              <h4 className={`font-semibold truncate ${isLocked ? 'text-stone-400' : 'text-stone-900'}`} style={{ fontFamily: "'Outfit', sans-serif" }}>{mod.title}</h4>
              <p className={`text-xs mt-1 line-clamp-2 ${isLocked ? 'text-stone-400' : 'text-stone-500'}`}>{mod.description}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge className={`${meta.badge} text-[10px]`}>{meta.label}</Badge>
                <span className="text-[11px] text-stone-400">{mod.lesson_count} {mod.module_type === 'modular' ? 'sections' : 'lessons'}</span>
                {mod.status === 'approved' && mod.signoff?.reviewed_by_name && (
                  <span className="text-[11px] text-stone-400">by {mod.signoff.reviewed_by_name}</span>
                )}
              </div>
              {mod.status === 'rejected' && mod.signoff?.review_note && (
                <p className="text-xs text-red-600 bg-red-50 rounded-lg px-2 py-1.5 mt-2" data-testid={`path-module-note-${mod.id}`}>
                  Manager note: {mod.signoff.review_note}
                </p>
              )}
              {isLocked && <p className="text-[11px] text-stone-400 mt-2">{mod.lock_reason}</p>}
            </div>
            {!isLocked && (
              <Button size="sm" variant={mod.status === 'approved' ? 'outline' : 'default'}
                className={`rounded-xl text-xs shrink-0 ${mod.status === 'approved' ? 'border-stone-200' : 'bg-[#1B2A4A] hover:bg-[#243658] text-white'}`}
                onClick={(e) => { e.stopPropagation(); navigate(`/training/${mod.id}`); }} data-testid={`path-module-open-${mod.id}`}>
                {mod.status === 'approved' ? <><RotateCcw className="w-3 h-3 mr-1" /> Revisit</> : mod.status === 'pending_signoff' ? 'View' : mod.status === 'rejected' ? 'Retake' : mod.status === 'in_progress' ? 'Continue' : 'Start'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function PathCard({ path, navigate, delay }) {
  const pct = path.total ? Math.round((path.approved_count / path.total) * 100) : 0;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }} data-testid={`learning-path-${path.id}`}>
      <div className="flex items-end justify-between mb-3 px-1">
        <div>
          <div className="flex items-center gap-2">
            <Route className="w-4 h-4 text-[#D9480F]" />
            <h3 className="text-base font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{path.name}</h3>
            {path.complete && <Badge className="bg-green-100 text-green-700 text-[10px]">Complete</Badge>}
          </div>
          {path.description && <p className="text-xs text-stone-500 mt-0.5">{path.description}</p>}
        </div>
        <span className="text-xs font-medium text-stone-500" data-testid={`learning-path-count-${path.id}`}>{path.approved_count}/{path.total} signed off</span>
      </div>
      <div className="h-1.5 w-full bg-stone-200 rounded-full mb-4 overflow-hidden">
        <motion.div className="h-full bg-[#1B2A4A] rounded-full" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} />
      </div>
      {path.schedule?.length > 0 && <TrainingSchedule schedule={path.schedule} navigate={navigate} />}
      {path.modules.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-stone-300 bg-stone-50"><CardContent className="p-5 text-center text-sm text-stone-500">No modules added to this path yet</CardContent></Card>
      ) : path.modules.map((m, i) => <PathModuleRow key={m.id} mod={m} index={i} isLast={i === path.modules.length - 1} navigate={navigate} />)}
    </motion.div>
  );
}

export default function LearningPathView({ paths, navigate }) {
  if (!paths || paths.length === 0) {
    return (
      <Card className="bg-white rounded-2xl border border-stone-200" data-testid="no-learning-path">
        <CardContent className="p-8 text-center">
          <BookOpen className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-600 font-medium">No learning path assigned yet</p>
          <p className="text-sm text-stone-400 mt-1">Your manager will set up your role-based training path.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-8" data-testid="learning-path-view">
      {paths.map((p, i) => <PathCard key={p.id} path={p} navigate={navigate} delay={i * 0.1} />)}
    </div>
  );
}
